import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  Package, ShoppingCart, ClipboardList, TrendingUp, LifeBuoy,
  Eye, ChevronRight, Lock, ArrowRight, ArrowLeft, Plus, Minus,
  Check, CheckCircle2, Sparkles, BarChart3, Printer, Calendar,
  Map as MapIcon, LayoutDashboard,
} from 'lucide-react'
import { supabase } from '../lib/supabase'
import { applyBrand, DEFAULT_BRAND } from '../lib/brand'
import { isStaffViewer } from '../lib/staffViewer'
import ReportiqDashboard from '../components/ReportiqDashboard'
import DataProtectionNotice from '../components/DataProtectionNotice'

// Full OrderiQ nav (ordering + reporting). "ReportiQ" is the branded label
// for the reports section — same underlying pane, product-styled.
const NAV_ORDERING = [
  { key: 'place',    label: 'Place Order',  icon: ShoppingCart },
  { key: 'products', label: 'Products',     icon: Package },
  { key: 'orders',   label: 'My Orders',    icon: ClipboardList },
  { key: 'activity', label: 'Activity',     icon: TrendingUp },
  { key: 'reports',  label: 'ReportiQ',     icon: BarChart3 },
  { key: 'help',     label: 'How to Order', icon: LifeBuoy },
]
// ReportiQ-only nav (no ordering surfaces). The dashboard IS ReportiQ, so
// there's a single reporting entry point rather than a Dashboard tab AND a
// duplicate ReportiQ tab pointing at the older ReportsPane.
const NAV_REPORTING = [
  { key: 'dashboard', label: 'ReportiQ',  icon: BarChart3 },
  { key: 'products',  label: 'Products',  icon: Package },
  { key: 'help',      label: 'Help',      icon: LifeBuoy },
]

function fmtMoney(n) {
  if (n == null) return '—'
  return '£' + Number(n).toFixed(2)
}

// Trial orders live in sessionStorage keyed by preview token so they persist
// across tab navigation but not across browsers/sessions. Nothing ever hits
// the database — the preview is anonymous by design.
function trialKey(token) { return `trial_orders_${token}` }
function readTrials(token) {
  try { return JSON.parse(sessionStorage.getItem(trialKey(token)) || '[]') } catch { return [] }
}
function saveTrials(token, list) {
  try { sessionStorage.setItem(trialKey(token), JSON.stringify(list)) } catch {}
}

export default function PreviewLink() {
  const { token } = useParams()
  const [ctx, setCtx] = useState(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const [active, setActive] = useState('place') // reset based on ctx.mode after load
  const [applyModalOpen, setApplyModalOpen] = useState(false)
  const [entered, setEntered] = useState(() => {
    if (typeof window === 'undefined') return false
    return sessionStorage.getItem('preview_entered_' + (window.location.pathname || '')) === '1'
  })
  const [trials, setTrials] = useState(() => token ? readTrials(token) : [])
  // For group-scoped previews only: which site is currently active in the
  // sidebar picker. null = "All sites" combined (default). Ignored for
  // company-scoped previews.
  const [activeSiteId, setActiveSiteId] = useState(null)

  const enterPortal = () => {
    setEntered(true)
    try { sessionStorage.setItem('preview_entered_' + window.location.pathname, '1') } catch {}
  }
  const backToWelcome = () => {
    setEntered(false)
    try { sessionStorage.removeItem('preview_entered_' + window.location.pathname) } catch {}
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'auto' })
  }

  useEffect(() => {
    if (!token) return
    // p_staff_view: this browser has been marked as staff by the admin console,
    // so the view-counter bump is skipped. Complements the staff_view_ips
    // allowlist, which can't reach staff on home broadband or a phone.
    supabase.rpc('get_preview_context', {
      p_token: token,
      p_staff_view: isStaffViewer(),
    }).then(({ data, error: rpcErr }) => {
      setLoading(false)
      if (rpcErr) { setError(rpcErr.message || 'Preview failed to load'); return }
      if (!data?.ok) { setError(data?.error || 'Preview link invalid'); return }
      setCtx(data)
      if (data.brand) applyBrand(data.brand)
      // Reporting-only groups land on the ReportiQ dashboard, not Place Order.
      if (data.mode === 'reporting') setActive('dashboard')
    })
  }, [token])

  const addTrial = (order) => {
    const next = [order, ...trials]
    setTrials(next)
    saveTrials(token, next)
  }

  if (loading) return <SplashScreen title="Loading preview…" />
  if (error) {
    return (
      <SplashScreen title="Preview unavailable" body={error}
        note="Ask the person who shared this link to check if it's still valid — links can expire or be revoked." />
    )
  }
  if (!ctx) return null

  const { scope = 'company', mode = 'ordering', company, group, companies = [], brand, products, cards, sample_orders, label, expires_at } = ctx
  const isGroupScope = scope === 'group'
  const isReportingOnly = mode === 'reporting'
  const NAV = isReportingOnly ? NAV_REPORTING : NAV_ORDERING
  const productLabel = isReportingOnly ? 'ReportiQ' : 'OrderiQ'
  // For group scope, activeSiteId can be null (= All sites combined).
  // For company scope, we're always locked to that one site.
  const activeSite = isGroupScope
    ? (activeSiteId ? companies.find(c => c.id === activeSiteId) : null)
    : company
  const displayName = activeSite?.name || (isGroupScope ? 'All sites' : (company?.name || 'Preview'))
  const brandNameForBanner = brand?.name || group?.name || company?.name

  // For group scope, filter sample orders + trials to the active site if picked
  const filteredSampleOrders = (!isGroupScope || !activeSiteId)
    ? sample_orders
    : (sample_orders || []).filter(o => o.company_id === activeSiteId)
  const filteredTrials = (!isGroupScope || !activeSiteId)
    ? trials
    : trials.filter(t => t.company_id === activeSiteId)

  if (!entered) return (
    <WelcomeScreen
      brand={brand}
      company={company}
      group={group}
      companies={companies}
      isGroupScope={isGroupScope}
      mode={mode}
      label={label}
      onEnter={enterPortal}
    />
  )

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <aside className="w-64 shrink-0 flex flex-col text-white" style={{ backgroundColor: brand?.primary_color_dark || '#467D69' }}>
        <div className="px-6 py-5 border-b border-white/10">
          <button
            onClick={backToWelcome}
            title="Back to welcome screen"
            className="block w-full text-left rounded-md -mx-1 px-1 py-1 hover:bg-white/5 transition-colors"
          >
            {brand?.logo_url
              ? <div className="bg-white/95 rounded-lg p-2 flex items-center justify-center">
                  <img
                    src={brand.logo_url}
                    alt={brand?.name || 'Logo'}
                    style={{
                      width: '100%', maxWidth: 210, maxHeight: 120, height: 'auto', display: 'block',
                      objectFit: 'contain',
                      mixBlendMode: brand?.logo_on_white ? 'multiply' : 'normal',
                    }}
                  />
                </div>
              : <div className="text-white font-semibold text-xl">{brand?.name || 'Order Platform'}</div>
            }
            <p className="text-white/60 text-xs mt-2">{productLabel} · Preview</p>
          </button>
        </div>

        {/* Group-scope: site picker so the head-office viewer can drill into
            individual sites or stay on the All Sites rollup. */}
        {isGroupScope && companies.length > 0 && (
          <div data-tour="site-picker" className="px-3 py-3 border-b border-white/10">
            <label className="text-[10px] uppercase tracking-wide text-white/60 flex items-center gap-1 mb-1">
              <MapIcon className="w-3 h-3" /> Active site
            </label>
            <select
              value={activeSiteId || '__all__'}
              onChange={e => setActiveSiteId(e.target.value === '__all__' ? null : e.target.value)}
              className="w-full px-2 py-1.5 rounded-md bg-white/10 text-white text-sm border border-white/20 focus:outline-none focus:ring-2 focus:ring-white/40"
            >
              <option value="__all__" className="text-gray-900">— All sites ({companies.length}) —</option>
              {companies.map(c => (
                <option key={c.id} value={c.id} className="text-gray-900">{c.name}</option>
              ))}
            </select>
          </div>
        )}

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {NAV.map(item => (
            <button
              key={item.key}
              data-tour={`nav-${item.key}`}
              onClick={() => setActive(item.key)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left ${
                active === item.key ? 'bg-white/15 text-white' : 'text-white/70 hover:bg-white/10 hover:text-white'
              }`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
              {active === item.key && <ChevronRight className="w-4 h-4 ml-auto" />}
            </button>
          ))}
        </nav>
        <div className="px-6 py-4 border-t border-white/10 text-xs text-white/60">
          <div className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> Preview visitor</div>
          <div className="mt-1 text-white/40">Trial orders are saved in this browser only.</div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <div className="bg-purple-100 border-b border-purple-200 text-purple-900 text-xs px-4 lg:px-8 py-2 flex items-center gap-2">
          <Eye className="w-4 h-4" />
          <span>
            <strong>Preview mode.</strong>
            {isGroupScope
              ? <> You're looking at the <strong>{group?.name || brandNameForBanner}</strong> head-office view — a group-wide rollup of all {companies.length} sites. Switch to a specific site from the sidebar to focus on one.</>
              : <> You're looking at the {brandNameForBanner} {isReportingOnly ? 'reporting portal' : 'ordering portal'} exactly as a real user would see it.</>
            }
            {' '}{isReportingOnly ? 'This is a reporting-only view.' : 'Nothing you click here places a real order — trial orders are ephemeral and stored only in this browser.'}
            {label && <span className="ml-2 opacity-70">Shared as: <em>{label}</em></span>}
          </span>
        </div>

        <header className="bg-white border-b border-gray-200 px-4 lg:px-8 py-4 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-xl font-semibold text-gray-900 truncate">{displayName}</h2>
            <p className="text-xs text-gray-500">
              {NAV.find(n => n.key === active)?.label}
              {isGroupScope && !activeSiteId && <span> · {companies.length} sites combined</span>}
            </p>
          </div>
          <div className="shrink-0 flex items-center gap-2" title={isReportingOnly ? 'ReportiQ · Insights Today. Better Tomorrow.' : 'OrderiQ · Simpler Ordering. Greater Control.'}>
            <img
              src={isReportingOnly ? '/reportiq-logo.png' : '/orderiq-logo.png'}
              alt={productLabel}
              style={{ height: 36, width: 'auto', maxWidth: 180 }}
            />
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-8 overflow-auto">
          {active === 'dashboard' && (
            <ReportiqDashboard
              orders={sample_orders}
              companies={companies.length ? companies : (company ? [{ id: company.id, name: company.name }] : [])}
              group={group}
              brand={brand}
              mode={mode}
              onApplyForOrdering={() => setApplyModalOpen(true)}
            />
          )}
          {active === 'products' && <ProductsPane products={products} />}
          {active === 'place'    && (
            isGroupScope && !activeSiteId
              ? <PickSiteFirstPane companies={companies} onPick={setActiveSiteId} />
              : <PlacePane
                  cards={isGroupScope ? cards.filter(c => c.company_id === activeSiteId) : cards}
                  products={products}
                  brand={brand}
                  trials={filteredTrials}
                  activeSite={activeSite}
                  onSubmitTrial={(order) => { addTrial(order); setActive('orders') }}
                />
          )}
          {active === 'orders'   && <OrdersPane orders={filteredSampleOrders} trials={filteredTrials} showSite={isGroupScope && !activeSiteId} />}
          {active === 'activity' && <ActivityPane orders={filteredSampleOrders} trials={filteredTrials} />}
          {active === 'reports'  && <ReportsPane orders={filteredSampleOrders} trials={filteredTrials} company={activeSite || { name: group?.name || 'All sites' }} />}
          {active === 'help'     && <HelpPane brand={brand} mode={mode} />}
        </main>

        <PreviewFooter
          expires_at={expires_at}
          token={token}
          groupName={group?.name || brand?.name || 'the account'}
        />
      </div>

      {applyModalOpen && (
        <ApplyForOrderingModal
          token={token}
          groupName={group?.name || brand?.name || 'your account'}
          onClose={() => setApplyModalOpen(false)}
        />
      )}
    </div>
  )
}

function PreviewFooter({ expires_at, token, groupName }) {
  const [asking, setAsking] = useState(false)
  const [askDone, setAskDone] = useState(false)
  const [askErr, setAskErr] = useState('')
  const daysLeft = expires_at
    ? Math.max(0, Math.ceil((new Date(expires_at).getTime() - Date.now()) / 86400000))
    : null
  const expiryLabel = expires_at ? new Date(expires_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'
  const warn  = daysLeft != null && daysLeft <= 7
  const alarm = daysLeft != null && daysLeft <= 2
  const tone  = alarm ? 'text-red-600' : warn ? 'text-amber-600' : 'text-gray-500'

  async function requestExtension() {
    setAsking(true); setAskErr('')
    try {
      await supabase.functions.invoke('send-order-email', {
        body: {
          client: null,
          internal: {
            subject: `Preview link extension requested — ${groupName}`,
            html: `<p><strong>${groupName}</strong> has clicked <em>Request extension</em> on their preview link.</p><p>Link token: <code>${token}</code></p><p>Currently expires: <strong>${expiryLabel}</strong> (${daysLeft} day${daysLeft === 1 ? '' : 's'} left).</p><p>Open Admin → Preview Links to extend or reissue.</p>`,
          },
        },
      })
      setAskDone(true)
    } catch (e) {
      setAskErr(e.message || 'Could not send request — please contact Walter Geering directly.')
    } finally {
      setAsking(false)
    }
  }

  return (
    <footer className="border-t border-gray-200 bg-white px-4 lg:px-8 py-3 flex flex-col sm:flex-row items-center justify-between gap-2 text-[11px]">
      <div className={`flex items-center gap-2 ${tone}`}>
        <span>Preview link · expires {expiryLabel}</span>
        {daysLeft != null && (
          <span className={`px-2 py-0.5 rounded-full font-semibold ${
            alarm ? 'bg-red-50 text-red-700' : warn ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-600'
          }`}>
            {daysLeft === 0 ? 'expires today' : daysLeft === 1 ? '1 day left' : `${daysLeft} days left`}
          </span>
        )}
        {warn && !askDone && (
          <button
            onClick={requestExtension}
            disabled={asking}
            className="ml-1 underline text-brand-dark hover:text-brand font-medium disabled:opacity-50"
          >
            {asking ? 'Requesting…' : 'Request extension'}
          </button>
        )}
        {askDone && <span className="text-emerald-600 font-medium">✓ Walter Geering has been notified</span>}
        {askErr && <span className="text-red-600">{askErr}</span>}
      </div>
      <div className="text-gray-500 flex items-center gap-3">
        <a href="https://www.waltergeering.co.uk" target="_blank" rel="noopener noreferrer" className="hover:text-brand-dark inline-flex items-center gap-1">
          waltergeering.co.uk
        </a>
      </div>
    </footer>
  )
}

function ApplyForOrderingModal({ token, groupName, onClose }) {
  const [byEmail, setByEmail] = useState('')
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)
  const [err, setErr] = useState('')

  async function submit() {
    setSubmitting(true); setErr('')
    try {
      const { data, error } = await supabase.rpc('apply_for_ordering', { p_token: token, p_by_email: byEmail || null, p_note: note || null })
      if (error) throw error
      if (!data?.ok) throw new Error(data?.error || 'Could not submit')
      // Fire-and-forget email to WG sales — non-blocking.
      try {
        await supabase.functions.invoke('send-order-email', {
          body: {
            client: null,
            internal: {
              subject: `Order-portal application from ${groupName}`,
              html: `<p><strong>${groupName}</strong> has clicked "Apply for order portal" from their ReportiQ dashboard.</p>${byEmail ? `<p>Contact: <a href="mailto:${byEmail}">${byEmail}</a></p>` : ''}${note ? `<p><em>Note:</em> ${note.replace(/</g, '&lt;')}</p>` : ''}<p>Open Admin → Client groups → ${groupName} to switch mode to Ordering.</p>`,
            },
          },
        })
      } catch (_) { /* recorded in DB regardless */ }
      setDone(true)
    } catch (e) {
      setErr(e.message || 'Something went wrong')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
        {done ? (
          <div className="text-center py-4">
            <div className="w-14 h-14 rounded-full bg-brand-50 text-brand-dark mx-auto flex items-center justify-center mb-3"><CheckCircle2 className="w-7 h-7" /></div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Application received</h3>
            <p className="text-sm text-gray-600">Walter Geering has been notified. Someone will be in touch to talk through switching your sites onto ordering.</p>
            <button onClick={onClose} className="btn-primary mt-4">Close</button>
          </div>
        ) : (
          <>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Apply for order portal</h3>
            <p className="text-sm text-gray-600 mb-4">
              Let Walter Geering know you'd like your sites to place their own orders through this platform.
              We'll be in touch to switch it on — no immediate change to your account.
            </p>
            <label className="block mb-3">
              <span className="text-xs text-gray-500 block mb-1">Best contact email (optional)</span>
              <input type="email" value={byEmail} onChange={e => setByEmail(e.target.value)} placeholder="you@company.co.uk" className="input-field" />
            </label>
            <label className="block mb-4">
              <span className="text-xs text-gray-500 block mb-1">Anything specific to mention? (optional)</span>
              <textarea value={note} onChange={e => setNote(e.target.value)} rows={3} placeholder="e.g. Start with 3 sites, then roll out to the rest in the new year." className="input-field" />
            </label>
            {err && <p className="text-sm text-red-600 mb-3">{err}</p>}
            <div className="flex gap-2 justify-end">
              <button onClick={onClose} className="btn-secondary">Cancel</button>
              <button onClick={submit} disabled={submitting} className="btn-primary">
                {submitting ? 'Sending…' : 'Send application'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function WelcomeScreen({ brand, company, group, companies = [], isGroupScope = false, mode = 'ordering', label, onEnter }) {
  const [dpAccepted, setDpAccepted] = useState(false)
  const targetName = brand?.name || (isGroupScope ? group?.name : company?.name)
  const isReportingOnly = mode === 'reporting'
  const productLabel = isReportingOnly ? 'Reporting Portal' : 'Ordering Portal'
  const productTagline = isReportingOnly
    ? 'Your Spend. Your Sites. Your Products. Clearly Reported.'
    : 'Your Orders. Your Way. Real Time.'
  const hasBg = !!brand?.login_bg_url
  const bgStyle = hasBg
    ? {
        backgroundImage: `linear-gradient(rgba(0,0,0,0.35), rgba(0,0,0,0.55)), url(${brand.login_bg_url})`,
        backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat',
      }
    : { background: `linear-gradient(135deg, rgb(var(--brand-light) / 0.5) 0%, rgb(var(--brand) / 0.15) 100%)` }
  return (
    <div className="min-h-screen flex items-center justify-center p-4 py-8" style={bgStyle}>
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden my-auto">
        <div className="p-8 text-center">
          {brand?.logo_url
            ? <img src={brand.logo_url} alt={targetName || 'Logo'} className="mx-auto mb-4"
                style={{ maxWidth: 200, height: 'auto', display: 'block', marginLeft: 'auto', marginRight: 'auto' }} />
            : <div className="text-2xl font-semibold text-brand-dark mb-2">{targetName}</div>
          }
          <h1 className="text-lg font-semibold text-gray-800 mt-4 mb-1">
            {isGroupScope
              ? <>Welcome to the {targetName} Head Office view</>
              : <>Welcome to the {targetName} {productLabel}</>
            }
          </h1>
          <p className="text-xs uppercase tracking-wider text-brand-dark mb-4 font-medium">{productTagline}</p>
          <p className="text-sm text-gray-500 mb-6">
            {isReportingOnly
              ? <>See total spend across your sites, understand which products are being used where, and download an annual spend report at any time.</>
              : isGroupScope
                ? <>Browse the group rollup — all {companies.length} sites combined. Reports, activity and order history are aggregated. Switch to a specific site any time to drill in.</>
                : <>Browse products, see your order history, and place a trial order to try the flow.</>
            }
          </p>
          <div className="mb-5">
            <DataProtectionNotice accepted={dpAccepted} onChange={setDpAccepted} />
          </div>

          <button
            onClick={() => dpAccepted && onEnter()}
            disabled={!dpAccepted}
            aria-disabled={!dpAccepted}
            title={dpAccepted ? '' : 'Please acknowledge the Data Protection Notice first'}
            className={`w-full py-3 rounded-xl font-semibold text-sm inline-flex items-center justify-center gap-2 transition-all ${
              dpAccepted
                ? 'text-white shadow-lg hover:shadow-xl active:scale-[0.98] cursor-pointer'
                : 'text-gray-400 bg-gray-100 cursor-not-allowed'
            }`}
            style={dpAccepted
              ? { backgroundColor: brand?.primary_color_dark || brand?.primary_color || '#467D69' }
              : undefined}
          >
            {dpAccepted
              ? <>Enter portal <ArrowRight className="w-4 h-4" /></>
              : <><Lock className="w-4 h-4" /> Enter portal</>
            }
          </button>
          <p className="text-[11px] text-gray-400 mt-2">
            Access is subject to acknowledgement of these requirements.
          </p>
          <p className="text-xs text-gray-400 mt-4 flex items-center justify-center gap-1">
            <Eye className="w-3 h-3" /> Preview — trial orders are saved in this browser only.
          </p>
          <div className="mt-6 pt-5 border-t border-gray-100 flex flex-col items-center gap-1">
            <p className="text-[10px] uppercase tracking-wider text-gray-400">Powered by</p>
            <img
              src={isReportingOnly ? '/reportiq-logo.png' : '/orderiq-logo.png'}
              alt={isReportingOnly ? 'ReportiQ' : 'OrderiQ'}
              style={{ height: 32, width: 'auto', maxWidth: 160 }}
            />
          </div>
        </div>
        {label && (
          <div className="bg-gray-50 border-t border-gray-100 px-6 py-3 text-center text-[11px] text-gray-500">
            Shared as: <em>{label}</em>
          </div>
        )}
      </div>
    </div>
  )
}

function SplashScreen({ title, body, note }) {
  useEffect(() => { applyBrand(DEFAULT_BRAND) }, [])
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
        <Lock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h1 className="text-lg font-semibold text-gray-800 mb-2">{title}</h1>
        {body && <p className="text-sm text-gray-600 mb-2">{body}</p>}
        {note && <p className="text-xs text-gray-400">{note}</p>}
      </div>
    </div>
  )
}

function ProductsPane({ products }) {
  if (!products || products.length === 0) {
    return <div className="card"><p className="text-sm text-gray-500">No products loaded yet.</p></div>
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {products.map(p => (
        <div key={p.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="aspect-square bg-gray-50 flex items-center justify-center overflow-hidden p-3">
            {p.image_url
              ? <img src={p.image_url} alt="" className="max-w-full max-h-full object-contain" />
              : <Package className="w-10 h-10 text-gray-300" />
            }
          </div>
          <div className="p-4">
            <div className="font-semibold text-gray-800 text-sm">{p.name}</div>
            {p.sku && <div className="text-[11px] text-gray-400 mt-0.5">{p.sku}</div>}
            {p.description && <div className="text-xs text-gray-500 mt-1 line-clamp-2">{p.description}</div>}
            <div className="mt-2 flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-800">{fmtMoney(p.price)}</span>
              {p.unit && <span className="text-xs text-gray-500">per {p.unit}</span>}
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

function PlacePane({ cards, products, brand, onSubmitTrial, trials = [], activeSite = null }) {
  const primary = brand?.primary_color || '#76B39D'
  const primaryDark = brand?.primary_color_dark || '#467D69'
  const [pickedCardId, setPickedCardId] = useState(null)
  const [quantities, setQuantities] = useState({}) // product_id -> qty
  const [submitted, setSubmitted] = useState(null)

  const loadFromTrial = (trial) => {
    const q = {}
    for (const l of trial.lines || []) q[l.product_id] = l.qty
    setQuantities(q)
    setPickedCardId(cards[0]?.id || '__any__')
  }

  const setQty = (id, delta) => setQuantities(q => {
    const next = Math.max(0, (q[id] || 0) + delta)
    return { ...q, [id]: next }
  })

  const total = useMemo(() =>
    products.reduce((sum, p) => sum + (quantities[p.id] || 0) * Number(p.price || 0), 0),
    [quantities, products]
  )
  const lineCount = Object.values(quantities).filter(v => v > 0).length

  const submit = () => {
    if (lineCount === 0) return
    const lines = products
      .filter(p => (quantities[p.id] || 0) > 0)
      .map(p => ({
        product_id: p.id,
        sku: p.sku, name: p.name, price: p.price, unit: p.unit,
        qty: quantities[p.id],
        subtotal: (quantities[p.id] || 0) * Number(p.price || 0),
      }))
    const now = new Date()
    const yr = now.getFullYear()
    const seq = String(now.getTime()).slice(-4)
    const order = {
      id: `trial-${now.getTime()}`,
      reference: `TRIAL-${yr}-${seq}`,
      status: 'sent',
      placed_at: now.toISOString(),
      customer_po: `TRIAL-${seq}`,
      is_trial: true,
      lines,
      total,
      // For group-scope previews, tag the trial with which site it was placed
      // on so the sidebar site picker can filter to it later.
      company_id: activeSite?.id || null,
      company_name: activeSite?.name || null,
    }
    setSubmitted(order)
    setQuantities({})
    setPickedCardId(null)
    setTimeout(() => { onSubmitTrial(order); setSubmitted(null) }, 1600)
  }

  if (submitted) {
    return (
      <div className="max-w-md mx-auto text-center py-12">
        <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center animate-pulse" style={{ backgroundColor: primary + '30' }}>
          <CheckCircle2 className="w-10 h-10" style={{ color: primaryDark }} />
        </div>
        <h2 className="text-xl font-bold text-gray-800 mt-4">Trial order submitted!</h2>
        <p className="text-sm text-gray-500 mt-1">
          Reference: <strong>{submitted.reference}</strong>
        </p>
        <p className="text-xs text-gray-400 mt-3">Redirecting to My Orders…</p>
      </div>
    )
  }

  // No card picked yet — show the two-panel start
  if (!pickedCardId) {
    return (
      <div className="max-w-3xl space-y-4">
        <div className="card">
          <h3 className="h-display-sm mb-2">How would you like to <span className="accent">start?</span></h3>
          <p className="text-sm text-gray-500 mb-4">Start fresh with a product card, or duplicate a past trial order.</p>
          {cards?.length > 0 ? (
            <>
              <div className="section-label mb-2">Start a new order</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {cards.map(c => (
                  <button
                    key={c.id}
                    onClick={() => setPickedCardId(c.id)}
                    className="text-left p-4 rounded-xl border border-gray-200 bg-white hover:border-brand hover:shadow-sm transition-all flex items-center gap-3 group"
                  >
                    <ClipboardList className="w-6 h-6" style={{ color: primary }} />
                    <div className="flex-1">
                      <div className="font-medium text-gray-800">{c.name}</div>
                      {c.description && <div className="text-xs text-gray-500 mt-0.5">{c.description}</div>}
                    </div>
                    <ArrowRight className="w-5 h-5 text-gray-300 group-hover:text-brand transition" />
                  </button>
                ))}
              </div>
            </>
          ) : (
            <p className="text-sm text-gray-500 italic">No order cards set up for this site yet.</p>
          )}
        </div>

        <div className="card">
          <div className="section-label mb-2">Or duplicate a previous trial order</div>
          <p className="text-sm text-gray-500 mb-3">
            Same products &amp; quantities as before — quick when you order the same set regularly.
            You can tweak anything before submitting.
          </p>
          {trials.length === 0 ? (
            <p className="text-sm text-gray-500 italic py-3 px-4 bg-gray-50 rounded-lg">
              No previous trial orders yet. Submit one below and it'll appear here so you can copy it in one click.
            </p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {trials.slice(0, 5).map(t => (
                <li key={t.id}>
                  <button
                    onClick={() => loadFromTrial(t)}
                    className="w-full text-left py-3 px-2 -mx-2 rounded-lg hover:bg-gray-50 flex items-center gap-3 group transition-colors"
                  >
                    <Package className="w-5 h-5 shrink-0" style={{ color: primary }} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-800 text-sm truncate">
                        {t.reference}
                        <span className="ml-2 text-[10px] uppercase font-bold text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded">Trial</span>
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(t.placed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                        {' · '}{t.lines?.length || 0} item{t.lines?.length === 1 ? '' : 's'}
                        {' · '}{fmtMoney(t.total)}
                      </div>
                    </div>
                    <span className="text-xs font-medium opacity-70 group-hover:opacity-100 inline-flex items-center gap-1" style={{ color: primaryDark }}>
                      Copy to new order <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    )
  }

  // Card picked — show products with qty steppers + submit
  return (
    <div className="max-w-4xl space-y-4">
      <div className="card">
        <button onClick={() => setPickedCardId(null)} className="text-xs text-gray-500 hover:text-gray-700 inline-flex items-center gap-1 mb-3">
          <ArrowLeft className="w-3.5 h-3.5" /> Change card
        </button>
        <h3 className="font-semibold text-gray-800 mb-1">Add products to your trial order</h3>
        <p className="text-sm text-gray-500 mb-4">Set quantities with + and −, then submit.</p>
        {products.length === 0 ? (
          <p className="text-sm text-gray-500 italic">No products on this card yet.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {products.map(p => {
              const qty = quantities[p.id] || 0
              return (
                <li key={p.id} className="py-4 flex items-start gap-4">
                  {p.image_url
                    ? <img src={p.image_url} alt="" className="w-24 h-24 sm:w-28 sm:h-28 rounded-lg object-contain bg-gray-50 p-1 shrink-0 border border-gray-100" />
                    : <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-lg bg-gray-50 flex items-center justify-center shrink-0 border border-gray-100"><Package className="w-10 h-10 text-gray-300" /></div>
                  }
                  <div className="flex-1 min-w-0 pt-0.5">
                    <div className="font-semibold text-gray-800 text-sm">{p.name}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {p.sku}{p.unit ? ` · ${p.unit}` : ''}{p.pack_size ? ` · ${p.pack_size}/box` : ''}
                    </div>
                    {p.description && (
                      <p className="text-xs text-gray-600 mt-1.5 leading-relaxed line-clamp-3">{p.description}</p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0 pt-0.5">
                    <div className="text-sm font-semibold text-gray-800 tabular-nums">{fmtMoney(p.price)}</div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setQty(p.id, -1)}
                        disabled={qty === 0}
                        className="w-8 h-8 rounded-md border border-gray-200 flex items-center justify-center hover:bg-gray-50 disabled:opacity-40"
                      ><Minus className="w-4 h-4" /></button>
                      <div className="w-10 text-center font-medium tabular-nums">{qty}</div>
                      <button
                        onClick={() => setQty(p.id, +1)}
                        className="w-8 h-8 rounded-md border border-gray-200 flex items-center justify-center hover:bg-gray-50"
                      ><Plus className="w-4 h-4" /></button>
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>

      <div className="card flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="section-label">Trial order total</div>
          <div className="text-2xl font-semibold text-gray-800 tabular-nums">{fmtMoney(total)}</div>
          <div className="text-xs text-gray-500">{lineCount} item{lineCount === 1 ? '' : 's'}</div>
        </div>
        <button
          onClick={submit}
          disabled={lineCount === 0}
          className="px-6 py-3 rounded-xl text-white font-semibold text-sm inline-flex items-center gap-2 shadow-lg hover:shadow-xl active:scale-[0.98] disabled:opacity-40 transition-all"
          style={{ backgroundColor: primaryDark }}
        >
          <Sparkles className="w-4 h-4" /> Submit trial order
        </button>
      </div>
    </div>
  )
}

function PickSiteFirstPane({ companies, onPick }) {
  return (
    <div className="card max-w-2xl">
      <h3 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
        <MapIcon className="w-5 h-5 text-brand" /> Pick a site to place an order
      </h3>
      <p className="text-sm text-gray-600 mb-4">
        You're viewing the head-office rollup right now — great for reports and history,
        but orders always belong to one specific site. Pick a site below (or use the site
        picker in the sidebar):
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {companies.map(c => (
          <button
            key={c.id}
            onClick={() => onPick(c.id)}
            className="text-left p-3 rounded-lg border border-gray-200 bg-white hover:border-brand hover:shadow-sm transition"
          >
            <div className="font-medium text-gray-800 text-sm">{c.name}</div>
            {c.contact_name && <div className="text-xs text-gray-500 mt-0.5">Contact: {c.contact_name}</div>}
          </button>
        ))}
      </div>
    </div>
  )
}

function OrdersPane({ orders, trials, showSite = false }) {
  const all = [...(trials || []), ...(orders || [])]
  if (all.length === 0) {
    return (
      <div className="card">
        <p className="text-sm text-gray-500 italic">No orders yet — place one from Place Order to see it here.</p>
      </div>
    )
  }
  return (
    <div className="card">
      <h3 className="font-semibold text-gray-800 mb-3">Recent orders</h3>
      <ul className="divide-y divide-gray-100">
        {all.map(o => (
          <li key={o.id} className="py-3 flex items-center gap-3">
            <ClipboardList className="w-5 h-5 text-gray-400" />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-gray-800 text-sm">
                {o.reference || '(draft)'}
                {o.is_trial && <span className="ml-2 text-[10px] uppercase font-bold text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded">Trial</span>}
                {showSite && o.company_name && <span className="ml-2 text-xs text-gray-500 font-normal">· {o.company_name}</span>}
              </div>
              <div className="text-xs text-gray-500">
                {o.customer_po ? `PO ${o.customer_po} · ` : ''}
                {o.placed_at ? new Date(o.placed_at).toLocaleDateString() : (o.created_at ? new Date(o.created_at).toLocaleDateString() : '—')}
                {o.total != null && ` · ${fmtMoney(o.total)}`}
                {o.lines?.length ? ` · ${o.lines.length} line${o.lines.length === 1 ? '' : 's'}` : ''}
              </div>
            </div>
            <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700">{o.status}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function ActivityPane({ orders, trials }) {
  const all = [...(trials || []), ...(orders || [])]
  return (
    <div className="max-w-3xl space-y-3">
      <div className="card">
        <h3 className="font-semibold text-gray-800 mb-1 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-brand" /> Activity summary
        </h3>
        <p className="text-sm text-gray-500 mb-4">Quick stats about ordering activity at this site.</p>
        <div className="grid grid-cols-3 gap-3">
          <StatTile label="Orders (all time)" value={all.length} />
          <StatTile label="Trial orders" value={(trials || []).length} />
          <StatTile label="Real orders" value={(orders || []).length} />
        </div>
      </div>
    </div>
  )
}

function StatTile({ label, value }) {
  return (
    <div className="stat-tile">
      <div className="section-label">{label}</div>
      <div className="stat-value">{value}</div>
    </div>
  )
}

function ReportsPane({ orders, trials, company }) {
  const today = new Date().toISOString().slice(0, 10)
  const ninetyDaysAgo = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
  const [from, setFrom] = useState(ninetyDaysAgo)
  const [to, setTo] = useState(today)
  const [previewMode, setPreviewMode] = useState(false)
  const [siteFilter, setSiteFilter] = useState('__all__') // company_id or '__all__'

  // List of unique sites present in the data, for the site-filter dropdown.
  const siteOptions = useMemo(() => {
    const map = new Map()
    for (const o of orders || []) {
      const id = o.company_id
      if (id && !map.has(id)) map.set(id, o.company_name || o.site_name || 'Unknown site')
    }
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]))
  }, [orders])
  const hasMultipleSites = siteOptions.length > 1
  const filteredOrders = useMemo(() => {
    if (siteFilter === '__all__') return orders || []
    return (orders || []).filter(o => o.company_id === siteFilter)
  }, [orders, siteFilter])
  const filterLabel = siteFilter === '__all__'
    ? (company?.name || 'All sites')
    : (siteOptions.find(([id]) => id === siteFilter)?.[1] || 'Selected site')

  // Both trial (client-side) and real orders now come with line detail from
  // the RPC, so spend/units/per-product all count both.
  const inRange = (dateStr) => {
    if (!dateStr) return false
    const d = new Date(dateStr).toISOString().slice(0, 10)
    return d >= from && d <= to
  }
  const rangedTrials = (trials || []).filter(t => inRange(t.placed_at))
  const rangedSample = filteredOrders.filter(o => inRange(o.placed_at || o.created_at))
  const allInRange = [...rangedTrials, ...rangedSample]

  // Sage records the same SKU on two bases: sometimes qty=400 × price=£2.48
  // (unit basis), sometimes qty=0.1 × price=£2480 (case basis). Summing qty
  // naively is nonsense. Money is always right, so we derive:
  //   unit_price   = min(price_at_order) across the product's history
  //   units        = total_spend / unit_price
  //   cases        = units / pack_size
  // Both numbers can be computed only when we know the true unit price
  // (a small number, not a case list price).
  const perProduct = useMemo(() => {
    const map = new Map()
    for (const o of allInRange) {
      for (const l of o.lines || []) {
        const key = l.sku || l.name
        if (!map.has(key)) map.set(key, { name: l.name, sku: l.sku, packSize: l.pack_size || null, spend: 0, prices: [], orderIds: new Set() })
        const row = map.get(key)
        const qty = Number(l.qty || 0)
        const price = Number(l.price || 0)
        row.spend += Number(l.subtotal || qty * price)
        if (price > 0) row.prices.push(price)
        row.orderIds.add(o.id)
      }
    }
    return [...map.values()]
      .map(r => {
        const unitPrice = r.prices.length ? Math.min(...r.prices) : null
        const units = unitPrice ? r.spend / unitPrice : null
        const cases = (units !== null && r.packSize) ? units / r.packSize : null
        return { ...r, unitPrice, units, cases, orderCount: r.orderIds.size }
      })
      .sort((a, b) => b.spend - a.spend)
  }, [allInRange])

  const summary = useMemo(() => {
    let totalSpend = 0
    let totalUnits = 0
    let totalCases = 0
    let anyUnitsUnknown = false
    let anyCasesUnknown = false
    for (const r of perProduct) {
      totalSpend += r.spend
      if (r.units !== null) totalUnits += r.units
      else anyUnitsUnknown = true
      if (r.cases !== null) totalCases += r.cases
      else anyCasesUnknown = true
    }
    return {
      orderCount: allInRange.length,
      totalSpend,
      totalCases,
      totalUnits,
      anyUnitsUnknown,
      anyCasesUnknown,
      distinctProducts: perProduct.length,
    }
  }, [perProduct, allInRange])

  // Per-site breakdown. Uses the same unit-price map derived by perProduct so
  // spend/units/cases roll up consistently. Only shows sites present in the
  // range window. Sorted by spend desc.
  const perSite = useMemo(() => {
    // First derive the same unit price + pack size per SKU from all lines in
    // range, so a site's Units number can never disagree with the perProduct
    // table above.
    const unitPriceBySku = new Map()
    const packSizeBySku  = new Map()
    for (const o of allInRange) {
      for (const l of o.lines || []) {
        const key = l.sku || l.name
        if (l.price && Number(l.price) > 0) {
          const cur = unitPriceBySku.get(key)
          unitPriceBySku.set(key, cur == null ? Number(l.price) : Math.min(cur, Number(l.price)))
        }
        if (l.pack_size) packSizeBySku.set(key, Number(l.pack_size))
      }
    }
    const map = new Map()
    for (const o of allInRange) {
      const siteName = o.company_name || o.site_name || 'Unknown site'
      if (!map.has(siteName)) map.set(siteName, { name: siteName, orders: 0, spend: 0, units: 0, cases: 0, unitsUnknown: false, casesUnknown: false })
      const row = map.get(siteName)
      row.orders += 1
      for (const l of o.lines || []) {
        const key = l.sku || l.name
        const qty = Number(l.qty || 0)
        const subtotal = Number(l.subtotal || qty * (Number(l.price) || 0))
        row.spend += subtotal
        const up = unitPriceBySku.get(key)
        const ps = packSizeBySku.get(key)
        if (up) {
          const u = subtotal / up
          row.units += u
          if (ps) row.cases += u / ps
          else row.casesUnknown = true
        } else {
          row.unitsUnknown = true
          row.casesUnknown = true
        }
      }
    }
    return [...map.values()].sort((a, b) => b.spend - a.spend)
  }, [allInRange])

  const fmtMoney = (n) => '£' + Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  // Sage stores qty in "cases" and can be fractional (e.g. 0.2 = 20% of a case).
  // Trim trailing zeros so 8.10 -> 8.1 and 5.00 -> 5.
  const fmtQty = (n) => {
    const v = Math.round(Number(n || 0) * 100) / 100
    return Number.isInteger(v) ? v.toLocaleString('en-GB') : v.toLocaleString('en-GB', { maximumFractionDigits: 2 })
  }
  const fmtDate = (s) => new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })

  // Full-screen "preview report" — shows the report as an A4-ish document with a
  // floating toolbar so the head-office user can eyeball the printout before
  // hitting Print. Everything below the controls card renders the same, so
  // both views stay perfectly consistent.
  const previewFrame = previewMode ? (
    <div className="fixed inset-0 z-50 bg-gray-800/70 overflow-auto p-4 sm:p-8" onClick={() => setPreviewMode(false)}>
      <div
        className="mx-auto bg-white shadow-2xl rounded-md p-6 sm:p-10 max-w-[820px] space-y-4"
        onClick={e => e.stopPropagation()}
      >
        <div className="border-b border-gray-200 pb-3 mb-2">
          <div className="text-xs uppercase tracking-wide text-gray-500">Ordering Report</div>
          <h1 className="text-2xl font-bold text-gray-900">{filterLabel}</h1>
          <p className="text-sm text-gray-600 mt-1">{fmtDate(from)} — {fmtDate(to)}</p>
        </div>
        <ReportBody
          summary={summary}
          perSite={perSite}
          perProduct={perProduct}
          allInRange={allInRange}
          fmtMoney={fmtMoney}
          fmtQty={fmtQty}
          fmtDate={fmtDate}
        />
      </div>
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-white shadow-xl rounded-full px-2 py-2 flex items-center gap-2 border border-gray-200">
        <button onClick={() => setPreviewMode(false)} className="px-4 py-2 rounded-full text-sm font-medium text-gray-700 hover:bg-gray-100">
          Close preview
        </button>
        <button onClick={() => window.print()} className="px-5 py-2 rounded-full text-sm font-semibold bg-brand text-white hover:bg-brand-dark">
          <Printer className="w-4 h-4 inline mr-1" /> Print / save PDF
        </button>
      </div>
    </div>
  ) : null

  return (
    <div className="space-y-4 max-w-5xl print:max-w-none">
      {previewFrame}
      <div className="card print:hidden">
        <div className="flex items-center gap-2 mb-3">
          <BarChart3 className="w-5 h-5 text-brand" />
          <h3 className="font-semibold text-gray-800">Ordering report</h3>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Pick a date range to see how much you've spent, how many orders, and which products.
          Print or save as PDF to share with your team.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
          <label>
            <span className="text-xs text-gray-500 block mb-1">From</span>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)} className="input-field" />
          </label>
          <label>
            <span className="text-xs text-gray-500 block mb-1">To</span>
            <input type="date" value={to} onChange={e => setTo(e.target.value)} className="input-field" />
          </label>
          {hasMultipleSites && (
            <label>
              <span className="text-xs text-gray-500 block mb-1">Site</span>
              <select value={siteFilter} onChange={e => setSiteFilter(e.target.value)} className="input-field">
                <option value="__all__">— All sites ({siteOptions.length}) —</option>
                {siteOptions.map(([id, name]) => (
                  <option key={id} value={id}>{name}</option>
                ))}
              </select>
            </label>
          )}
          <div className={`flex gap-2 ${hasMultipleSites ? 'lg:justify-self-end' : 'sm:col-span-2 lg:col-span-2 lg:justify-self-end'}`}>
            <button onClick={() => setPreviewMode(true)} className="btn-secondary">
              <BarChart3 className="w-4 h-4" /> Preview
            </button>
            <button onClick={() => window.print()} className="btn-primary">
              <Printer className="w-4 h-4" /> Print / save PDF
            </button>
          </div>
        </div>
      </div>

      <div className="hidden print:block mb-4">
        <h1 className="text-2xl font-bold text-gray-900">Ordering Report — {filterLabel}</h1>
        <p className="text-sm text-gray-600 mt-1">{fmtDate(from)} — {fmtDate(to)}</p>
      </div>

      <ReportBody
        summary={summary}
        perSite={perSite}
        perProduct={perProduct}
        allInRange={allInRange}
        fmtMoney={fmtMoney}
        fmtQty={fmtQty}
        fmtDate={fmtDate}
      />
    </div>
  )
}

// Shared report content. Used both on the Reports tab directly and inside
// the full-screen Preview modal so what the user sees on-screen matches
// what gets printed.
function ReportBody({ summary, perSite, perProduct, allInRange, fmtMoney, fmtQty, fmtDate }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <div className="stat-tile min-w-0">
          <div className="section-label flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Total spend</div>
          <div className="text-2xl font-bold text-gray-900 tabular-nums leading-tight break-words">{fmtMoney(summary.totalSpend)}</div>
        </div>
        <div className="stat-tile min-w-0">
          <div className="section-label flex items-center gap-1"><ClipboardList className="w-3 h-3" /> Orders</div>
          <div className="text-2xl font-bold text-gray-900 tabular-nums leading-tight">{summary.orderCount}</div>
        </div>
        <div className="stat-tile min-w-0">
          <div className="section-label flex items-center gap-1"><Package className="w-3 h-3" /> Cases</div>
          <div className="text-2xl font-bold text-gray-900 tabular-nums leading-tight">{fmtQty(summary.totalCases)}</div>
        </div>
        <div className="stat-tile min-w-0" title={summary.anyUnitsUnknown ? 'Some products have no pack size set, so units are undercounted.' : ''}>
          <div className="section-label flex items-center gap-1"><Package className="w-3 h-3" /> Units{summary.anyUnitsUnknown && <span className="text-amber-600">*</span>}</div>
          <div className="text-2xl font-bold text-gray-900 tabular-nums leading-tight">{fmtQty(summary.totalUnits)}</div>
        </div>
        <div className="stat-tile min-w-0">
          <div className="section-label flex items-center gap-1"><Package className="w-3 h-3" /> Distinct products</div>
          <div className="text-2xl font-bold text-gray-900 tabular-nums leading-tight">{summary.distinctProducts}</div>
        </div>
      </div>
      {summary.anyUnitsUnknown && (
        <p className="text-xs text-amber-700 -mt-1">
          * Some products don't have a pack size on file yet, so their units aren't counted (cases still are).
        </p>
      )}

      {perSite.length > 1 && (
        <div className="card">
          <h3 className="font-semibold text-gray-800 mb-3">By site</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase text-gray-500 border-b border-gray-100">
                  <th className="text-left py-2">Site</th>
                  <th className="text-right py-2 w-20">Orders</th>
                  <th className="text-right py-2 w-24">Cases</th>
                  <th className="text-right py-2 w-24">Units</th>
                  <th className="text-right py-2 w-32">Total spend</th>
                </tr>
              </thead>
              <tbody>
                {perSite.map((s, i) => (
                  <tr key={i} className="border-b border-gray-50">
                    <td className="py-2 font-medium text-gray-800">{s.name}</td>
                    <td className="text-right tabular-nums text-gray-700">{s.orders}</td>
                    <td className="text-right tabular-nums text-gray-700">
                      {s.casesUnknown ? <span className="text-gray-400" title="Some products lack a pack size">—</span> : fmtQty(s.cases)}
                    </td>
                    <td className="text-right tabular-nums text-gray-700">
                      {s.unitsUnknown ? <span className="text-gray-400" title="Some products lack a unit price">—</span> : fmtQty(s.units)}
                    </td>
                    <td className="text-right tabular-nums font-medium text-gray-800">{fmtMoney(s.spend)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="font-semibold text-gray-800">
                  <td className="pt-3">All sites</td>
                  <td className="pt-3 text-right tabular-nums">{summary.orderCount}</td>
                  <td className="pt-3 text-right tabular-nums">{fmtQty(summary.totalCases)}</td>
                  <td className="pt-3 text-right tabular-nums">{fmtQty(summary.totalUnits)}</td>
                  <td className="pt-3 text-right tabular-nums">{fmtMoney(summary.totalSpend)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      <div className="card">
        <h3 className="font-semibold text-gray-800 mb-3">Products ordered</h3>
        {perProduct.length === 0 ? (
          <p className="text-sm text-gray-500 italic">
            No orders with line detail in this date range. Submit a trial order from Place Order to see it here.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase text-gray-500 border-b border-gray-100">
                  <th className="text-left py-2">Product</th>
                  <th className="text-right py-2 w-16">Orders</th>
                  <th className="text-right py-2 w-24" title="Auto-detected as the lowest price seen for this product across history">Unit price</th>
                  <th className="text-right py-2 w-20">Cases</th>
                  <th className="text-right py-2 w-20">Units</th>
                  <th className="text-right py-2 w-28">Total spend</th>
                </tr>
              </thead>
              <tbody>
                {perProduct.map((r, i) => (
                  <tr key={i} className="border-b border-gray-50">
                    <td className="py-2">
                      <div className="font-medium text-gray-800">{r.name}</div>
                      {r.sku && <div className="text-xs text-gray-500">{r.sku}</div>}
                    </td>
                    <td className="text-right tabular-nums text-gray-700">{r.orderCount}</td>
                    <td className="text-right tabular-nums text-gray-500">
                      {r.unitPrice ? fmtMoney(r.unitPrice) : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="text-right tabular-nums text-gray-700">
                      {r.cases !== null ? fmtQty(r.cases) : <span className="text-gray-400" title="Pack size not set">—</span>}
                    </td>
                    <td className="text-right tabular-nums text-gray-700">
                      {r.units !== null ? fmtQty(r.units) : <span className="text-gray-400" title="Unit price not detected">—</span>}
                    </td>
                    <td className="text-right tabular-nums font-medium text-gray-800">{fmtMoney(r.spend)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="font-semibold text-gray-800">
                  <td className="pt-3 text-right pr-3" colSpan={5}>Total</td>
                  <td className="pt-3 text-right tabular-nums">{fmtMoney(summary.totalSpend)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {allInRange.length > 0 && (
        <div className="card print:break-before-page">
          <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <Calendar className="w-4 h-4" /> Orders in this period
          </h3>
          <ul className="divide-y divide-gray-100">
            {allInRange.map(o => (
              <li key={o.id} className="py-2 flex items-center gap-3 text-sm">
                <span className="font-medium text-gray-800">{o.reference || '—'}</span>
                {o.is_trial && <span className="text-[10px] uppercase font-bold text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded">Trial</span>}
                <span className="text-gray-500">{o.customer_po ? `PO ${o.customer_po}` : '—'}</span>
                <span className="text-gray-500 ml-auto">{o.placed_at ? fmtDate(o.placed_at) : (o.created_at ? fmtDate(o.created_at) : '—')}</span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">{o.status}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

const REPORTIQ_HELP_HTML = `
<h3>Using ReportiQ — a quick guide</h3>
<p>ReportiQ shows you what your sites have spent with Walter Geering, by site and by product, across whatever time period you choose. It's read-only reporting — you can't place orders here, but you can see everything you've bought.</p>

<h3>The Dashboard</h3>
<ol>
  <li><strong>Pick your period.</strong> Use the <em>Period</em> dropdown for quick presets (Year to date, Last 12 months, Last quarter, Previous year) or set custom From/To dates.</li>
  <li><strong>Pick your site.</strong> If your account covers several sites, the <em>Site</em> dropdown at the top and the Active-site picker in the sidebar both let you focus on one, or stay on <em>All sites</em> for the group rollup.</li>
  <li><strong>Read the numbers.</strong> Five headline tiles at the top: Total Spend, Active Sites, Average per Site, Distinct Products, and Year-on-Year (when we have enough history).</li>
  <li><strong>See the trends.</strong> The Monthly Spend Trend chart shows every month in your range. Hover a bar for the exact figure.</li>
  <li><strong>Understand your mix.</strong> "Spend by Category" splits your total into Toiletries / Cleaning / Welcome Packs etc. "Top Products" ranks the SKUs by spend.</li>
  <li><strong>Compare sites.</strong> The "By Site" table lists every site, ranked by spend, with a Variance column showing which sites are up or down year on year.</li>
</ol>

<h3>Print, save, or share</h3>
<p>The <strong>Print / save PDF</strong> button turns the dashboard into a polished report with an executive summary at the top — ready to email round or drop into a board pack. <strong>Excel</strong> gives you the raw numbers (By Site / By Category / Top Products) as a CSV that opens in Excel.</p>

<h3>Take the tour</h3>
<p>The <em>Take a quick tour</em> button on the dashboard walks you through everything above with each section spotlighted. It's about 90 seconds — you can re-open it any time.</p>

<h3>Frequently asked questions</h3>

<p><strong>Why does the year-on-year comparison say "—"?</strong><br/>
We only show YoY when we have at least £500 and 3 orders in the prior-year window. Once you have a full year of history the comparison lights up automatically.</p>

<p><strong>Can I export the data to my own systems?</strong><br/>
Yes — the Excel button downloads a CSV covering all three tables. If you need a different cut, contact Walter Geering and we can produce a bespoke export.</p>

<p><strong>Can our sites place orders through this?</strong><br/>
Not on a reporting-only account. If you'd like to enable ordering, click <strong>Apply for order portal</strong> on the dashboard — Walter Geering will be in touch to switch it on.</p>

<p><strong>Something looks wrong / a number is missing.</strong><br/>
Get in touch with your Walter Geering contact — the reporting data comes straight from our order system, so anything odd is worth flagging so we can put it right.</p>

<h3>Who to contact</h3>
<p>For anything you can't see in the app, please email <a href="mailto:sales@waltergeering.co.uk">sales@waltergeering.co.uk</a> or call us on <strong>01233 636666</strong>. We're happy to help.</p>
`

function HelpPane({ brand, mode = 'ordering' }) {
  const isReporting = mode === 'reporting'
  const html = isReporting
    ? REPORTIQ_HELP_HTML
    : (brand?.client_help_html || REPORTIQ_HELP_HTML)
  return (
    <div className="max-w-3xl">
      <div className="card">
        <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <LifeBuoy className="w-5 h-5 text-brand" />
          {isReporting ? 'How to use ReportiQ' : 'How to place an order'}
        </h3>
        <div
          className="prose prose-sm max-w-none
            [&_h3]:text-brand-dark [&_h3]:font-semibold [&_h3]:text-lg [&_h3]:mt-8 [&_h3]:mb-3 [&_h3:first-child]:mt-0
            [&_p]:my-3 [&_p]:leading-relaxed [&_p]:text-gray-700
            [&_ol]:my-3 [&_ol]:space-y-2 [&_ul]:my-3 [&_ul]:space-y-2
            [&_li]:leading-relaxed [&_li]:text-gray-700
            [&_a]:text-brand-dark [&_a]:underline hover:[&_a]:text-brand
            [&_strong]:text-gray-900"
          dangerouslySetInnerHTML={{ __html: html }} />
      </div>
    </div>
  )
}
